/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be.ignitor

import kotlin.random.Random

/**
 * Context for Ignitor rendering. Created ONCE per voice, mutated per block.
 * No reinstantiation in the hot path.
 *
 * RULE: No Long anywhere in audio hot paths. Long is boxed in Kotlin/JS = severe perf degradation.
 * Int frames: max ~2.1B frames = ~12.4 hours at 48kHz — more than enough for any voice.
 */
class IgniteContext(
    // ── Static per voice (set at creation, never changes) ──────────────────────
    /** Audio sample rate in Hz */
    val sampleRate: Int,
    /**
     * Total gate duration in frames (scheduled, before release).
     *
     * Deliberately NOT moved by [io.peekandpoke.klang.audio_be.voices.Voice.releaseGate]: it is
     * the `accelerate` glide base (the glide reaches its target at this gate and holds it through the
     * release, decision D2, for both doors since pitch pipeline step 3), and retro-shrinking it would
     * jump the glide progress and leap the pitch. On held realtime voices `accelerate` is inert by decision
     * (KLANG-00157 (realtime note off gate release)).
     */
    val voiceDurationFrames: Int,
    // ── Per-block input inside a voice (see the KDoc) ──────────────────────────
    /**
     * Frame (relative to voice start) when gate ends and release begins. Ignitors read it per block,
     * never keep a copy.
     *
     * Inside a voice this is NOT a second home of the gate: the ignite stage (`IgniteRenderer`) derives it
     * from the voice's limits (`VoiceLimits`, the one home) before every `generate` call, so a realtime
     * note-off reaches the ignitors on the next block and nothing else writes it. The constructor value
     * only serves a context used without a voice (specs, benchmarks), which sets it here or by hand.
     */
    var gateEndFrame: Int,
    // ── Static per voice (set at creation, never changes) ──────────────────────
    /** Shared scratch buffer pool for binary composition operators */
    val scratchBuffers: ScratchBuffers,
    /**
     * THE VOICE'S random stream — derived per voice from the playback's `coreRandom`
     * (`PlaybackCtx`), shared by every draw in this voice's sub-graph (generate-time drift
     * construction reads it here; build-time consumers get the SAME instance via
     * `IgnitorBuildCache.random`). One instance per voice is safe because in-graph draw
     * order is deterministic; deriving per voice is what makes draw order BETWEEN voices
     * irrelevant and playback runs bit-reproducible. No default: every caller passes a stream
     * (production the voice's own, a spec a seeded `Random(n)`), so a forgotten one is a compile
     * error, never the process-wide `Random`.
     */
    val random: Random,

    // ── Mutable per block (updated by caller before each generate() call) ──────
    // NOTE: `offset` and `length` are NOT here — they live in the body, because they carry
    // custom setters that keep [windowEnd] in sync, and a constructor property cannot have one.
    /**
     * Frames since voice start (monotonic, updated once per block), counted AT buffer index
     * [offset] — NOT at index 0. A voice's first `generate` call sees 0. Every consumer adds its
     * own `i - offset` on top (`AdsrIgnitor`, `IgnitorFilters`, `PitchModFactories`); computing it
     * at index 0 made the first block of a mid-block onset run a negative clock, which clamped to
     * silence and then stepped. See `IgniteOnsetOffsetSpec`.
     */
    var voiceElapsedFrames: Int = 0,
    /**
     * Per-sample phase-increment multipliers (1.0 = no change), or null.
     * MUST be at least (offset + length) elements long when non-null.
     *
     * **[ModApplyingIgnitor] is the one source of a non-null value** (in the engine): it sets it around the source it
     * wraps and restores it after; [ModBlockingIgnitor] only clears it around an absolute-frequency source, and
     * restores it too. Null at the root of a
     * voice's tree: since pitch pipeline step 5 the voice hands the tree no pitch modulation (`IgniteRenderer` never
     * writes it); every pitch modulation (vibrato, accelerate, pitchEnvelope, FM, pitchMod, the `classic()` pitch
     * stages) is a node of the tree, resolved at build time into the [ModApplyingIgnitor] that sets this field for
     * its source.
     */
    var phaseMod: DoubleArray? = null,
    // ── Static per voice (set at creation, never changes) ──────────────────────
    /**
     * Whether [voiceDurationFrames] is the note's real gate length: false for a held realtime voice (a live note
     * started with no gate length, KLANG-00157 (realtime note off gate release)), whose gate is the far held-voice
     * horizon until its note-off, and the note-off never moves [voiceDurationFrames]. Read by [ProgressIgnitor],
     * which reads 0 throughout on such a voice (KLANG-00245 step 7f). Defaults to true: a scheduled voice, or a
     * context used without a voice (specs, benchmarks).
     */
    val gateKnown: Boolean = true,
) {
    /**
     * The voice's own context when this one is an oversampling region's (`OversampleRegionIgnitor`), null for the voice's
     * own. Sources stay at the base rate (KLANG-00214 (oversampling regions) section 10): a source inside a region
     * renders in this context and is converted up where it enters (`LiftedSourceIgnitor`). Set by the region before
     * every block, with the window the voice's context has then.
     */
    var rootContext: IgniteContext? = null

    // ── Mutable per block (updated by caller before each generate() call) ──────

    /** Start index in buffer for this block. Moved via [updateOffsetAndLength] / [updateOffset]. */
    var offset: Int = 0
        private set

    /** Number of samples to generate. Moved via [updateOffsetAndLength] / [updateLength]. */
    var length: Int = 0
        private set

    /**
     * One past the last buffer index this block touches, i.e. `offset + length`.
     *
     * All three window fields are `private set` so this one CANNOT go stale: the only way in is
     * the update functions below, which recompute it once. That matters more than the arithmetic
     * it saves — a wrong render window is the block-framing bug class
     * (KLANG-00156 (block framing invariance)), and a hand-maintained copy would invite it back.
     *
     * Read it instead of recomputing the sum: ~120 render methods used to open with
     * `val end = ctx.offset + ctx.length`, once per node per block.
     */
    var windowEnd: Int = 0
        private set

    /**
     * Moves the whole render window — the normal per-block update.
     *
     * [windowEnd] is recomputed ONCE here. Assigning the two fields separately would compute it
     * twice and, in between, leave the context describing a window that never existed.
     */
    fun updateOffsetAndLength(offset: Int, length: Int) {
        this.offset = offset
        this.length = length
        this.windowEnd = offset + length
    }

    /** Moves the window start, keeping [length]. */
    fun updateOffset(offset: Int) {
        this.offset = offset
        this.windowEnd = offset + length
    }

    /** Resizes the window, keeping [offset]. */
    fun updateLength(length: Int) {
        this.length = length
        this.windowEnd = offset + length
    }

    // ── Computed properties (derived from above, no storage) ───────────────────

    /** Pre-computed Double to avoid repeated Int→Double conversion in hot loops */
    val sampleRateD: Double = sampleRate.toDouble()

    /** The rate an [AnalogDrift] lane built for this context steps at: once per block. */
    val driftStepRate: Int = analogDriftStepRate(sampleRate = sampleRate, blockFrames = scratchBuffers.blockFrames)

    /** Pre-computed Double to avoid repeated Int→Double conversion in hot loops */
    val voiceDurationFramesD: Double = voiceDurationFrames.toDouble()

    // Five block-start-only convenience accessors (voiceElapsedSecs, voiceDurationSecs,
    // voiceProgress, isInRelease, releaseProgress) were DELETED here 2026-08-28 with zero callers
    // (block-framing ledger E6). They were shaped exactly like the bug class this file's clock got
    // burned by: a time value that silently means "at ctx.offset" but reads like "now". If a
    // per-sample variant is ever needed, it must take the sample offset explicitly — name it
    // `...At(sampleOffset)` — never a bare property.
}
